import Model, { ID, Integer, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/Model'

export const SEAT_TYPES = {
  REGULAR_SEAT: 'regular-seat',
  RECLINER_SEAT: 'recliner-seat',
  WHEELCHAIR: 'wheelchair',
  COMPANION: 'companion',
  COUCH: 'couch', // Used for Seat legend
  COUCH_LEFT: 'couch-left',
  COUCH_CENTER: 'couch-center',
  COUCH_RIGHT: 'couch-right',
  COUCH_CORNER_LEFT: 'couch-corner-left',
  COUCH_CORNER_RIGHT: 'couch-corner-right',
  COUCH_CORNER_SET: 'couch-corner-set', // Used for Seat legend
  COUCH_VERTICAL_EAST_LEFT: 'couch-vertical-east-left',
  COUCH_VERTICAL_EAST_CENTER: 'couch-vertical-east-center',
  COUCH_VERTICAL_WEST_CENTER: 'couch-vertical-west-center',
  COUCH_VERTICAL_WEST_RIGHT: 'couch-vertical-west-right',
  TABLE: 'table',
  TABLE_LEFT: 'table-left',
  TABLE_CENTER: 'table-center',
  TABLE_RIGHT: 'table-right',
  TABLE_SET: 'table-set', // Used for Seat legend
  AISLE: 'aisle'
}
SEAT_TYPES.ALL = _.values(SEAT_TYPES)
SEAT_TYPES.OVERRIDES_OPTIONS = _.without(SEAT_TYPES.ALL, SEAT_TYPES.COUCH, SEAT_TYPES.COUCH_CORNER_SET, SEAT_TYPES.TABLE_SET)
SEAT_TYPES.OPTIONS = _.without(SEAT_TYPES.ALL, SEAT_TYPES.AISLE, SEAT_TYPES.COUCH, SEAT_TYPES.COUCH_CORNER_SET, SEAT_TYPES.TABLE_SET)

export const AVAILABILITY_TYPES = {
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable',
  BROKEN: 'broken',
  HOUSE: 'house',
  RESERVED: 'reserved',
  AISLE: 'aisle',
  SELECTED: 'selected',
  SOCIAL_DISTANCING: 'social-distancing',
  PRIOR_SEAT: 'prior-seats'
}
AVAILABILITY_TYPES.ALL = _.values(AVAILABILITY_TYPES)
AVAILABILITY_TYPES.OPTIONS = _.without(AVAILABILITY_TYPES.ALL, AVAILABILITY_TYPES.AVAILABLE, AVAILABILITY_TYPES.AISLE, AVAILABILITY_TYPES.SOCIAL_DISTANCING, AVAILABILITY_TYPES.PRIOR_SEAT, AVAILABILITY_TYPES.SELECTED)

export const overrideInitialValues = {
  available: null,
  availabilityType: null,
  availableStartTime: null,
  availableEndTime: null,
  seatType: null,
  seatName: null,
  reserveDuringMarathons: null,
  limitTicketTypes: null,
  ticketTypeIds: [],
  customSeatType: null,
  heldSeatSectionName: null,
  confirmationRequired: null,
  confirmationMessage: null
}

export default class SeatChart extends Model {
  static name = 'SeatChart'

  static paginate = true
  static deletable = true
  static duplicatable = true
  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Ticketing]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      name: {
        type: String,
        required: true
      },
      displayOrder: {
        type: Integer,
        default: 100
      },
      seatCount: {
        type: Integer,
        output: true
      },
      seatChartConfiguration: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            rowCount: 8,
            columnCount: 8,
            primarySeatType: 'regular-seat',
            rowAisles: [],
            columnAisles: [],
            overrides: [],
            socialDistancing: false,
            columnGap: 2,
            rowGap: 1,
            allowCorners: true,
            allowGaps: false,
            allowSeatOrphans: false,
            allowPartialCouchSale: false,
            descendingRows: false,
            reverseColumns: false
          }
        }
      },
      seatChart: {
        type: JsonArray,
        default () {
          return []
        }
      },
      heldSeatSectionNames: {
        type: JsonArray,
        output: true,
        excludeFromDefault: true
      },
      libraryParentId: {
        type: ID,
        output: true,
        excludeFromDefault: true
      },
      receiveLibraryUpdates: {
        type: Boolean,
        excludeFromDefault: true
      },
      libraryChildSiteIds: {
        type: [ID],
        output: true,
        excludeFromDefault: true
      },
      libraryDescendantSites: {
        type: JsonArray,
        output: true,
        excludeFromDefault: true
      }
    }

    this.queries.seatChartForShowing = {
      arguments: {
        showingId: {
          type: ID,
          required: true
        },
        orderId: {
          type: ID,
          required: false
        }
      },
      results: {
        seatChart: SeatChart
      }
    }
  }

  afterInflate () {
    this.initializeOptions()
  }

  initializeOptions () {
    if (!this.seatChartConfiguration) return

    _.each(this.seatChartConfiguration.overrides, override => {
      return _.extend({}, _.extend(_.cloneDeep(overrideInitialValues), override))
    })
  }

  get availableSeats () {
    return _(this.seatChart).flatten().filter('available').map('name').uniq().value()
  }

  get availableSeatsWithoutSocialDistancing () {
    return _(this.seatChart).flatten().filter(seat => seat.available || seat['social-distancing']).map('name').uniq().value()
  }

  seatLocation (seatName) {
    let colIndex
    const rowIndex = _.findIndex(this.seatChart, (row) => {
      colIndex = _.findIndex(row, (testSeat) => testSeat.name === seatName && testSeat.seatType !== 'aisle')
      return colIndex !== -1
    })
    return {
      colIndex,
      rowIndex
    }
  }

  seatObject (seatName) {
    const { colIndex, rowIndex } = this.seatLocation(seatName)
    return _.clone(this.seatChart[rowIndex][colIndex])
  }

  hasPartialCouchSaleError (selectedSeats) {
    if (this.seatChartConfiguration.allowPartialCouchSale) return false
    // For each row look for seat selections that have available seats directly between them
    for (let index = 0; index < selectedSeats.length; index++) {
      const selectedSeat = selectedSeats[index]

      const { colIndex, rowIndex } = this.seatLocation(selectedSeat)
      if (!this.seatChart[rowIndex]) {
        console.log(`Seat gap check out of bounds [${this.seatChart.length} <= ${rowIndex}]`)
        return
      }
      const seat = this.seatChart[rowIndex][colIndex]
      if (seat.seatType === 'couch-left') {
        const rightSeat = this.seatChart[rowIndex][colIndex + 1]

        if (!rightSeat || (rightSeat.seatType !== 'couch-right' && rightSeat.seatType !== 'couch-center' && rightSeat.seatType !== 'couch-corner-right')) continue

        if (rightSeat.available && !_.includes(selectedSeats, rightSeat.name)) {
          return true
        }
      }

      if (seat.seatType === 'couch-center') {
        const leftSeat = this.seatChart[rowIndex][colIndex - 1]
        if (leftSeat && (leftSeat.seatType === 'couch-left' || leftSeat.seatType === 'couch-corner-left')) {
          return leftSeat.available && !_.includes(selectedSeats, leftSeat.name)
        }

        const rightSeat = this.seatChart[rowIndex][colIndex + 1]
        if (rightSeat && (rightSeat.seatType === 'couch-right' || rightSeat.seatType === 'couch-corner-right')) {
          return rightSeat.available && !_.includes(selectedSeats, rightSeat.name)
        }
      }

      if (seat.seatType === 'couch-right') {
        const leftSeat = this.seatChart[rowIndex][colIndex - 1]
        if (!leftSeat || (leftSeat.seatType !== 'couch-left' && leftSeat.seatType !== 'couch-center' && leftSeat.seatType !== 'couch-corner-left')) continue

        if (leftSeat.available && !_.includes(selectedSeats, leftSeat.name)) {
          return true
        }
      }

      if (seat.seatType === 'couch-corner-left') {
        const rightSeat = this.seatChart[rowIndex][colIndex + 1]
        if (rightSeat && (rightSeat.seatType === 'couch-right' || rightSeat.seatType === 'couch-center')) {
          return rightSeat.available && !_.includes(selectedSeats, rightSeat.name)
        }

        const seatInFront = this.seatChart[rowIndex - 1][colIndex]
        if (seatInFront && (seatInFront.seatType === 'couch-vertical-east-left' || seatInFront.seatType === 'couch-vertical-east-center')) {
          return seatInFront.available && !_.includes(selectedSeats, seatInFront.name)
        }
      }

      if (seat.seatType === 'couch-corner-right') {
        const leftSeat = this.seatChart[rowIndex][colIndex - 1]
        if (leftSeat && (leftSeat.seatType === 'couch-left' || leftSeat.seatType === 'couch-center')) {
          return leftSeat.available && !_.includes(selectedSeats, leftSeat.name)
        }

        const seatInFront = this.seatChart[rowIndex - 1][colIndex]
        if (seatInFront && (seatInFront.seatType === 'couch-vertical-west-right' || seatInFront.seatType === 'couch-vertical-west-center')) {
          return seatInFront.available && !_.includes(selectedSeats, seatInFront.name)
        }
      }

      if (seat.seatType === 'couch-vertical-east-left') {
        const seatBehind = this.seatChart[rowIndex + 1][colIndex]

        if (!seatBehind || (seatBehind.seatType !== 'couch-vertical-east-center' && seatBehind.seatType !== 'couch-corner-left')) continue

        if (seatBehind.available && !_.includes(selectedSeats, seatBehind.name)) {
          return true
        }
      }

      if (seat.seatType === 'couch-vertical-east-center') {
        const seatInFront = this.seatChart[rowIndex - 1][colIndex]
        if (seatInFront && seatInFront.seatType === 'couch-vertical-east-left') {
          return seatInFront.available && !_.includes(selectedSeats, seatInFront.name)
        }

        const seatBehind = this.seatChart[rowIndex + 1][colIndex]
        if (seatBehind && seatBehind.seatType === 'couch-corner-left') {
          return seatBehind.available && !_.includes(selectedSeats, seatBehind.name)
        }
      }

      if (seat.seatType === 'couch-vertical-west-right') {
        const seatBehind = this.seatChart[rowIndex + 1][colIndex]

        if (!seatBehind || (seatBehind.seatType !== 'couch-vertical-west-center' && seatBehind.seatType !== 'couch-corner-right')) continue

        if (seatBehind.available && !_.includes(selectedSeats, seatBehind.name)) {
          return true
        }
      }

      if (seat.seatType === 'couch-vertical-west-center') {
        const seatInFront = this.seatChart[rowIndex - 1][colIndex]
        if (seatInFront && seatInFront.seatType === 'couch-vertical-west-right') {
          return seatInFront.available && !_.includes(selectedSeats, seatInFront.name)
        }

        const seatBehind = this.seatChart[rowIndex + 1][colIndex]
        if (seatBehind && seatBehind.seatType === 'couch-corner-right') {
          return seatBehind.available && !_.includes(selectedSeats, seatBehind.name)
        }
      }
    }

    return false
  }
}
